/*
 * What the installed app keeps offline is encrypted (ToR 1.0(xi), 4.2.8): AES-GCM-256 through WebCrypto.
 * The data key is random, made on this device and kept only wrapped — by the device's wrapping key,
 * which the server issues per device (decision §141: server-issued) and which lives in memory alone.
 * A device the server has wiped or blocked cannot get its wrapping key again, so its store is dark.
 */
const te = new TextEncoder();
const td = new TextDecoder();

export function randomBytes(n: number): Uint8Array {
  const b = new Uint8Array(n);
  crypto.getRandomValues(b);
  return b;
}

export function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

export function fromBase64(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export async function importAesKey(raw: Uint8Array, usages: KeyUsage[] = ['encrypt', 'decrypt', 'wrapKey', 'unwrapKey']): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, usages);
}

export async function generateDataKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
}

/** The data key wrapped by the device key: iv + ciphertext, for the store. */
export async function wrapDataKey(dataKey: CryptoKey, deviceKey: CryptoKey): Promise<Uint8Array> {
  const iv = randomBytes(12);
  const wrapped = new Uint8Array(await crypto.subtle.wrapKey('raw', dataKey, deviceKey, { name: 'AES-GCM', iv }));
  const out = new Uint8Array(12 + wrapped.length);
  out.set(iv); out.set(wrapped, 12);
  return out;
}

export async function unwrapDataKey(wrapped: Uint8Array, deviceKey: CryptoKey): Promise<CryptoKey> {
  const iv = wrapped.slice(0, 12);
  return crypto.subtle.unwrapKey('raw', wrapped.slice(12), deviceKey, { name: 'AES-GCM', iv }, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

/** iv (12 bytes) + ciphertext with tag. */
export async function encryptBytes(key: CryptoKey, plain: Uint8Array): Promise<Uint8Array> {
  const iv = randomBytes(12);
  const cipher = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plain));
  const out = new Uint8Array(12 + cipher.length);
  out.set(iv); out.set(cipher, 12);
  return out;
}

export async function decryptBytes(key: CryptoKey, sealed: Uint8Array): Promise<Uint8Array> {
  const iv = sealed.slice(0, 12);
  return new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, sealed.slice(12)));
}

export async function encryptJson(key: CryptoKey, value: unknown): Promise<Uint8Array> {
  return encryptBytes(key, te.encode(JSON.stringify(value)));
}

export async function decryptJson<T>(key: CryptoKey, sealed: Uint8Array): Promise<T> {
  return JSON.parse(td.decode(await decryptBytes(key, sealed))) as T;
}

/** PBKDF2-SHA-256 of a PIN with a salt, for the offline lock screen (ToR 4.3.2): only ever compared, never sent. */
export async function hashPin(pin: string, salt: Uint8Array, iterations = 150_000): Promise<string> {
  const base = await crypto.subtle.importKey('raw', te.encode(pin), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt, iterations }, base, 256);
  return toBase64(new Uint8Array(bits));
}
